import React, { useState } from 'react';
import { Button } from './ui/Button';
import { AlertTriangle, Info, CheckCircle, RotateCcw } from 'lucide-react';
const questions = ['Чи ставили Ви більше грошей, ніж могли дозволити собі втратити?', 'Чи потрібно було грати на більші суми, щоб отримати той самий рівень азарту?', 'Чи поверталися Ви до гри в наступні дні, щоб відіграти програні гроші?', 'Чи позичали Ви гроші або продавали щось, щоб мати змогу грати?', 'Чи відчували Ви, що можете мати проблему з азартними іграми?', 'Чи викликала Ваша гра стрес або проблеми зі здоров’ям (включаючи тривогу або напруження)?', 'Чи критикували інші люди Вас за гру в азартні ігри або казали, що Ви маєте проблему з грою в азартні ігри?', 'Чи викликала Ваша гра фінансові проблеми для Вас або Вашої сім’ї?', 'Чи відчували Ви провину через свою гру або її наслідки?'];
const options = [{
  label: 'Ніколи',
  value: 0
}, {
  label: 'Іноді',
  value: 1
}, {
  label: 'Часто',
  value: 2
}, {
  label: 'Майже завжди',
  value: 3
}];
export function PGSITestSection() {
  const [answers, setAnswers] = useState<Record<number, number>>({});
  const [showResult, setShowResult] = useState(false);
  const handleOptionSelect = (questionIndex: number, value: number) => {
    setAnswers(prev => ({
      ...prev,
      [questionIndex]: value
    }));
  };
  const isComplete = Object.keys(answers).length === questions.length;
  const calculateScore = () => {
    return Object.values(answers).reduce((sum, val) => sum + val, 0);
  };
  const getResult = (score: number) => {
    if (score === 0) {
      return {
        title: 'Відсутні ознаки проблемної гри',
        color: 'text-green-600',
        bgColor: 'bg-green-50',
        borderColor: 'border-green-200',
        description: 'Ваша ігрова поведінка не має ознак ризику. Продовжуйте грати відповідально.'
      };
    } else if (score >= 1 && score <= 2) {
      return {
        title: 'Низький рівень ризику',
        color: 'text-yellow-600',
        bgColor: 'bg-yellow-50',
        borderColor: 'border-yellow-200',
        description: 'Ви маєте низький рівень ризику. Будьте уважні до своїх звичок та контролюйте час і гроші, витрачені на гру.'
      };
    } else if (score >= 3 && score <= 7) {
      return {
        title: 'Помірний ризик',
        color: 'text-orange-600',
        bgColor: 'bg-orange-50',
        borderColor: 'border-orange-200',
        description: 'Ваша гра може викликати проблеми. Рекомендуємо переглянути своє ставлення до азартних ігор та встановими жорсткіші ліміти.'
      };
    } else {
      return {
        title: 'Високий ризик проблемної гри',
        color: 'text-red-600',
        bgColor: 'bg-red-50',
        borderColor: 'border-red-200',
        description: 'Ваша ігрова поведінка має високий ризик. Наполегливо рекомендуємо звернутися за професійною допомогою до психолога або спеціалізованих центрів підтримки.'
      };
    }
  };
  const resetTest = () => {
    setAnswers({});
    setShowResult(false);
  };
  return <section id="pgsi-test" className="py-20 bg-white" data-id="element-665">
      <div className="container mx-auto px-4 max-w-4xl" data-id="element-666">
        <div className="text-center mb-12" data-id="element-667">
          <h2 className="text-3xl md:text-5xl font-serif font-bold text-[#0A5E5C] mb-6" data-id="element-668">
            Тести для самоперевірки ризику ігрової залежності
          </h2>
          <div className="h-1 w-24 bg-[#D4A843] mx-auto rounded-full mb-8" data-id="element-669" />
        </div>

        {/* Warning Banner */}
        <div className="bg-yellow-50 border-l-4 border-yellow-400 p-6 rounded-r-xl mb-10" data-id="element-670">
          <div className="flex items-start gap-4" data-id="element-671">
            <AlertTriangle className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" data-id="element-672" />
            <div data-id="element-673">
              <h4 className="font-bold text-yellow-800 mb-2" data-id="element-674">Важливо</h4>
              <p className="text-yellow-700 text-sm leading-relaxed" data-id="element-675">
                Наведені нижче тести є лише інструментами для самодіагностики.
                Вони не є медичним діагнозом і не можуть замінити консультацію з
                психологом, психотерапевтом або іншим кваліфікованим
                спеціалістом. Якщо результати тесту викликають занепокоєння або
                Ви відчуваєте, що азартні ігри починають впливати на Ваше життя,
                рекомендується звернутися за професійною допомогою.
              </p>
            </div>
          </div>
        </div>

        {/* Info Card */}
        <div className="bg-gray-50 p-6 rounded-xl border border-gray-100 mb-12" data-id="element-676">
          <div className="flex items-start gap-4" data-id="element-677">
            <Info className="w-6 h-6 text-[#0A5E5C] flex-shrink-0 mt-1" data-id="element-678" />
            <div data-id="element-679">
              <h3 className="font-serif font-bold text-xl text-[#0A5E5C] mb-3" data-id="element-680">
                1. Problem Gambling Severity Index (PGSI)
              </h3>
              <p className="text-gray-600 text-sm mb-4 leading-relaxed" data-id="element-681">
                PGSI — це міжнародно визнаний інструмент оцінки ризику
                проблемної азартної поведінки.
              </p>
              <ul className="text-sm text-gray-500 space-y-1" data-id="element-682">
                <li data-id="element-683">
                  <span className="font-medium text-gray-700" data-id="element-684">
                    Розроблений:
                  </span>{' '}
                  Julie Ferris та Harold Wynne (2001)
                </li>
                <li data-id="element-685">
                  <span className="font-medium text-gray-700" data-id="element-686">
                    У рамках дослідження:
                  </span>{' '}
                  Canadian Problem Gambling Index (CPGI)
                </li>
                <li data-id="element-687">
                  <span className="font-medium text-gray-700" data-id="element-688">Замовник:</span>{' '}
                  Canadian Centre on Substance Abuse
                </li>
              </ul>
            </div>
          </div>
        </div>

        {/* Quiz Area */}
        {!showResult ? <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-6 md:p-10" data-id="element-689">
            <div className="mb-8" data-id="element-690">
              <h4 className="text-lg font-bold text-[#0A5E5C] mb-2" data-id="element-691">
                Інструкція:
              </h4>
              <p className="text-gray-600" data-id="element-692">
                Дайте відповідь на кожне запитання, оцінюючи свою поведінку за
                останні <span className="font-bold" data-id="element-693">12 місяців</span>.
              </p>
            </div>

            <div className="space-y-10" data-id="element-694">
              {questions.map((question, qIndex) => <div key={qIndex} className="border-b border-gray-100 pb-8 last:border-0 last:pb-0" data-id="element-695">
                  <h5 className="text-lg font-medium text-gray-800 mb-4" data-id="element-696">
                    <span className="text-[#D4A843] font-bold mr-2" data-id="element-697">
                      {qIndex + 1}.
                    </span>
                    {question}
                  </h5>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-3" data-id="element-698">
                    {options.map(option => <button key={option.value} onClick={() => handleOptionSelect(qIndex, option.value)} className={`py-3 px-4 rounded-lg border text-sm font-medium transition-all duration-200 ${answers[qIndex] === option.value ? 'bg-[#0A5E5C] border-[#0A5E5C] text-white shadow-md' : 'bg-white border-gray-200 text-gray-600 hover:border-[#0A5E5C] hover:text-[#0A5E5C]'}`} data-id="element-699">
                        {option.label}
                      </button>)}
                  </div>
                </div>)}
            </div>

            <div className="mt-12 pt-8 border-t border-gray-100 flex justify-center" data-id="element-700">
              <Button variant="primary" size="lg" disabled={!isComplete} onClick={() => setShowResult(true)} className="min-w-[200px]" data-id="element-701">
                Дізнатися результат
              </Button>
            </div>

            {!isComplete && <p className="text-center text-sm text-gray-400 mt-4" data-id="element-702">
                Дайте відповідь на всі запитання, щоб побачити результат (
                {Object.keys(answers).length} з {questions.length})
              </p>}
          </div> /* Results Area */ : <div className="bg-white rounded-2xl shadow-lg border border-gray-100 p-8 md:p-12 text-center animate-fade-in" data-id="element-703">
            <div className="w-20 h-20 bg-gray-50 rounded-full flex items-center justify-center mx-auto mb-6" data-id="element-704">
              <CheckCircle className="w-10 h-10 text-[#0A5E5C]" data-id="element-705" />
            </div>

            <h3 className="text-2xl font-serif font-bold text-gray-800 mb-2" data-id="element-706">
              Ваш результат:{' '}
              <span className="text-3xl text-[#0A5E5C]" data-id="element-707">
                {calculateScore()}
              </span>{' '}
              балів
            </h3>

            <div className={`mt-8 p-6 rounded-xl border ${getResult(calculateScore()).bgColor} ${getResult(calculateScore()).borderColor}`} data-id="element-708">
              <h4 className={`text-2xl font-bold mb-4 ${getResult(calculateScore()).color}`} data-id="element-709">
                {getResult(calculateScore()).title}
              </h4>
              <p className="text-gray-700 text-lg leading-relaxed" data-id="element-710">
                {getResult(calculateScore()).description}
              </p>
            </div>

            <div className="mt-10 flex justify-center" data-id="element-711">
              <Button variant="outline" onClick={resetTest} className="flex items-center gap-2" data-id="element-712">
                <RotateCcw className="w-4 h-4" data-id="element-713" />
                Пройти тест ще раз
              </Button>
            </div>
          </div>}
      </div>
    </section>;
}